// Library Methods
import { useCallback, useEffect, useMemo, useState } from "react";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";

// Hooks
import { useReportSectionData } from "src/services/exams/hooks/report-sections/useReportSectionData";
import useTokenAccess from "src/hooks/auth/useTokenAccess";

// API
import { getExternalExamCharts } from "src/services/exams/api";

// Types
import {
  EnrichedExternalExamType,
  EnrichedRetinaLogikSectionType,
  ExternalExamDataType,
  ExternalExamType,
  ProgressionAnalysisType,
  RetinaLogikExamSectionType,
} from "src/services/exams/types";

const PLATFORM_NAMES: Record<number, string> = {
  1: "ZeissForum",
  2: "TopconHarmony",
  3: "ZeissCirrus6000",
  4: "NidekOPDScanIII",
  5: "ZeissHFA2",
  6: "ZeissHFA3",
  7: "Opto",
  8: "MedmontM600",
  9: "FDT",
};

const platformName = (value: string | number | undefined) =>
  typeof value === "number"
    ? PLATFORM_NAMES[value] ?? "Undefined"
    : value ?? "";

const toEnrichedExternalExam = (
  externalExam: ExternalExamType
): EnrichedExternalExamType => {
  const flat = externalExam as unknown as ExternalExamDataType & {
    completionDate?: string;
  };

  return {
    ...externalExam,
    date: externalExam.date ?? flat.completionDate ?? "",
    sourcePlatform: platformName(externalExam.sourcePlatform),
    data: externalExam.data ?? flat,
    isRetinaLogikExam: false,
  };
};

type useEnrichedProgressionAnalysisType = {
  patientId: string;
  analysisData: ProgressionAnalysisType | undefined;
};

const useEnrichedProgressionAnalysis = ({
  patientId,
  analysisData,
}: useEnrichedProgressionAnalysisType) => {
  const { clinicInfo } = useClinicContext();

  const { mutateAsync: reportSectionMutate } = useReportSectionData(
    clinicInfo?.id
  );

  const { getToken } = useTokenAccess();

  const [isLoading, setIsLoading] = useState(false);
  const [hasRunFetches, setHasRunFetches] = useState(false);
  const [enrichedExams, setEnrichedExams] = useState<
    Array<EnrichedRetinaLogikSectionType>
  >([]);

  const baseExternalExams: Array<EnrichedExternalExamType> = useMemo(
    () => analysisData?.externalExams?.map(toEnrichedExternalExam) ?? [],
    [analysisData?.externalExams]
  );

  const [enrichedExternalExams, setEnrichedExternalExams] = useState<
    Array<EnrichedExternalExamType>
  >([]);

  useEffect(() => {
    setEnrichedExternalExams(baseExternalExams);
  }, [baseExternalExams]);

  useEffect(() => {
    if (!clinicInfo?.id || baseExternalExams.length === 0) return;

    let cancelled = false;
    const controller = new AbortController();

    const attachCharts = async () => {
      const token = await getToken();

      const withCharts = await Promise.all(
        baseExternalExams.map(async (externalExam) => {
          const charts = await getExternalExamCharts(
            token,
            clinicInfo.id,
            patientId,
            externalExam.id,
            controller.signal
          );

          return charts.length > 0 ? { ...externalExam, charts } : externalExam;
        })
      );

      if (!cancelled) setEnrichedExternalExams(withCharts);
    };

    attachCharts().catch(() =>
      console.error("Unable to fetch external exam charts.")
    );

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [baseExternalExams, clinicInfo?.id, patientId, getToken]);

  const getEnrichedExams = useCallback(async () => {
    setIsLoading(true);

    const enrichedExams: Array<EnrichedRetinaLogikSectionType> = [];

    if (analysisData?.exams) {
      try {
        // generate unique VF thresholding sections
        let vfSections: Array<RetinaLogikExamSectionType> = [];
        analysisData?.exams?.forEach((exam) => {
          exam?.visualFieldThresholdingSections?.forEach((examSection) => {
            if (
              examSection.eye === analysisData.eye &&
              !vfSections.find((section) => section.id === examSection.id)
            ) {
              vfSections = [
                ...vfSections,
                {
                  ...examSection,
                  examId: exam.id,
                  patientId,
                  patientAge: exam.age,
                  isRetinaLogikExam: true,
                },
              ];
            }
          });
        });

        // fetch data for each thresholding section
        await Promise.all(
          vfSections?.map(async (examSection) => {
            const reportData = await reportSectionMutate({
              patientId,
              examId: examSection.examId,
              sectionId: examSection.id,
            });

            if (reportData) {
              const parsedReportData = JSON.parse(reportData);

              enrichedExams.push({
                ...examSection,
                reportData: parsedReportData,
              });
            }
          })
        );
      } catch (e) {
        console.error("Unable to fetch enriched exams.");
      }
    }

    setIsLoading(false);
    setHasRunFetches(true);
    return enrichedExams;
  }, [patientId, analysisData?.exams, analysisData?.eye, reportSectionMutate]);

  useEffect(() => {
    if (analysisData?.exams && !hasRunFetches) {
      getEnrichedExams().then((res) => setEnrichedExams([...res]));
    }
  }, [analysisData?.exams, hasRunFetches, getEnrichedExams]);

  return { enrichedExams, enrichedExternalExams, isLoading };
};

export default useEnrichedProgressionAnalysis;
